import { apiClient } from '@shared/services/http/http.service.js'

const DEFAULT_SORT = 'firstName:asc'
export const MASS_UPDATE_LIMIT = 500
export const MASS_UPDATE_LIMIT_FOR_TABLE = 8

/**
 * Builds query params for GET /core/v1/identity/users/all
 * @param {object} filters - { search, permissionGroups[], defaultSite[], selectedArea[], selectedManager[], mfaEnabled, limit, sort }
 * @returns {URLSearchParams} Query parameters for the users request.
 */
function buildUsersAllParameters(filters = {}) {
  const parameters = new URLSearchParams()
  if (filters.search != null && String(filters.search).trim())
    parameters.set('search', String(filters.search).trim())
  if (Array.isArray(filters.permissionGroups) && filters.permissionGroups.length > 0)
    parameters.set('permissionGroups', filters.permissionGroups.join(','))
  if (Array.isArray(filters.defaultSite) && filters.defaultSite.length > 0)
    parameters.set('defaultSite', filters.defaultSite.join(','))
  if (Array.isArray(filters.selectedArea) && filters.selectedArea.length > 0)
    parameters.set('selectedArea', filters.selectedArea.join(','))
  if (Array.isArray(filters.selectedManager) && filters.selectedManager.length > 0)
    parameters.set('selectedManager', filters.selectedManager.join(','))
  if (filters.mfaEnabled === true || filters.mfaEnabled === false)
    parameters.set('mfaEnabled', String(filters.mfaEnabled))
  parameters.set('limit', String(filters.limit ?? 500))
  parameters.set('sort', filters.sort ?? DEFAULT_SORT)
  return parameters
}

/**
 * GET /core/v1/identity/users/all — list users with filters (for mass update screen).
 * Params: search, permissionGroups, defaultSite, selectedArea, selectedManager, mfaEnabled, limit, sort.
 * @param {object} filters - { nameOrEmail, groupIds, siteIds, areaIds, managerIds, noMfa }
 * @returns {Promise<Array>} list of user objects (docs)
 */

export async function fetchUsersAllForMassUpdate(filters = {}) {
  const parameters = buildUsersAllParameters({
    search: filters.nameOrEmail ?? undefined,
    permissionGroups: filters.groupIds ?? undefined,
    defaultSite: filters.siteIds ?? undefined,
    selectedArea: filters.areaIds ?? undefined,
    selectedManager: filters.managerIds ?? undefined,
    mfaEnabled: filters.noMfa === true ? false : undefined,
    limit: MASS_UPDATE_LIMIT + 1,
    sort: DEFAULT_SORT,
  })
  const { data } = await apiClient('core').get('identity/users/all', { params: parameters })
  if (Array.isArray(data))
    return data
  return data?.docs ?? []
}

export async function fetchCompanyUsers(parameters = {}, sortBy = DEFAULT_SORT) {
  const { permissionGroups, ...rest } = parameters
  const queryParameters = new URLSearchParams(rest)

  queryParameters.append('sort', sortBy)

  if (Array.isArray(permissionGroups) && permissionGroups.length > 0) {
    permissionGroups.forEach(id => queryParameters.append('permissionGroups', id))
  }

  const { data } = await apiClient('core').get('identity/users/all', {
    params: queryParameters,
  })

  if (Array.isArray(data)) {
    return data
  }

  return {
    docs: data?.docs ?? [],
    pagination: {
      page: data?.page ?? 1,
      limit: data?.limit ?? parameters?.limit ?? 10,
      totalDocs: data?.totalDocs ?? 0,
      totalPages: data?.totalPages ?? 0,
      hasNextPage: !!data?.hasNextPage,
      hasPrevPage: !!data?.hasPrevPage,
      nextPage: data?.nextPage ?? null,
      prevPage: data?.prevPage ?? null,
    },
  }
}

export async function listUsersCrossCompany(parameters = {}) {
  const query = {
    page: parameters.page ?? 1,
    limit: Math.min(parameters.limit ?? 10, 100),
    sort: parameters.sort ?? 'email:asc',
    ...(parameters.search && { q: parameters.search }),
  }
  const { data } = await apiClient('core').get('identity/users/cross-company', {
    params: query,
  })
  const users = Array.isArray(data?.data) ? data.data : []
  const meta = data?.meta ?? {}
  return { docs: users, meta }
}

export async function linkUserCrossCompany(userId, additionalData = {}) {
  const { data } = await apiClient('core').post('identity/users/cross-company', {
    userId,
    ...additionalData,
  })
  return data
}

export async function listUsersPlatformManagement(parameters = {}) {
  const queryParameters = new URLSearchParams(parameters)

  queryParameters.append('sort', 'firstName:asc')

  const { data } = await apiClient('core').get('identity/users/users-platform-management', {
    params: queryParameters,
  })

  if (Array.isArray(data)) {
    return data
  }

  return {
    docs: data?.docs ?? [],
    pagination: {
      page: data?.page ?? 1,
      limit: data?.limit ?? parameters?.limit ?? 10,
      totalDocs: data?.totalDocs ?? 0,
      totalPages: data?.totalPages ?? 0,
      hasNextPage: !!data?.hasNextPage,
      hasPrevPage: !!data?.hasPrevPage,
      nextPage: data?.nextPage ?? null,
      prevPage: data?.prevPage ?? null,
    },
  }
}

export async function fetchCompanyUser(id) {
  const response = await apiClient('core').get(`identity/users/${id}`)

  return response.data
}

export async function createUser(userData) {
  const response = await apiClient('core').post('identity/users', {
    ...userData,
  })

  return response.data
}

export async function updateUser(id, userData) {
  const response = await apiClient('core').put(`identity/users/${id}`, {
    ...userData,
  })

  return response.data
}

export async function deleteUser(id) {
  const response = await apiClient('core').delete(`identity/users/${id}`)
  return response.data
}

export async function updateUserMfa(id, enabled) {
  const response = await apiClient('core').patch(`identity/users/${id}/mfa`, {
    enabled,
  })
  return response.data
}

export async function fetchUserMfaStatus(id) {
  const response = await apiClient('core').get(`identity/users/${id}/mfa`)
  return response.data.data
}

/**
 * PATCH /core/v1/identity/users/mass-update
 * @param {string[]} userIds - list of user ids
 * @param {object} updates - permissionGroups, permissionGroupsToAdd, permissionGroupsToRemove, defaultSite, selectedArea, selectedManager, jobTitle, jobRegistration, department, position, defaultLanguage, subRoles, 'b2cSso.mfaEnabled'
 * @returns {Promise<unknown>} Mass update response data.
 */
export async function massUpdateUsers(userIds, updates) {
  const { data } = await apiClient('core').patch('identity/users/mass-update', {
    userIds,
    updates,
  })
  return data
}

export async function resendUserCredentials(userId) {
  const { data } = await apiClient('core').post(`identity/users/${userId}/resend-credentials`)
  return data
}
